import { Button } from "./ui/button";
import { Calendar, Phone, ShieldCheck, BadgeCheck, Flag } from "lucide-react";
import AnimatedChart from "./AnimatedChart";
import neilBrandLockup from "@/assets/neil-ai-brand-lockup.png";
import puduLogo from "@/assets/pudu-logo-official.png";
import welcomeVideo from "@/assets/welcome-video.mp4";
import AIChatBox from "./AIChatBox";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const HeroSection = () => {
  return (
    <div className="animate-fade-in neil-hero-wrapper mb-0">
      {/* Premium brand lockup panel */}
      <div className="neil-header-row mb-4 mt-1">
        <div
          className="relative w-full rounded-2xl border border-primary/25 backdrop-blur-md overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, hsl(210 83% 6% / 0.95) 0%, hsl(210 83% 3% / 0.95) 100%)",
            boxShadow:
              "0 0 40px hsl(var(--primary) / 0.25), inset 0 0 40px hsl(43 75% 55% / 0.08)",
          }}
        >
          <div className="pointer-events-none absolute -top-16 -left-16 h-40 w-40 rounded-full bg-primary/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-[hsl(43_75%_55%)]/25 blur-3xl" />

          {/* Hero brand image fills the panel */}
          <img
            src={neilBrandLockup}
            alt="Neil AI Global Solutions — Automate. Elevate. Dominate."
            className="relative w-full h-auto object-contain block"
            loading="eager"
          />

          {/* PUDU partner strip */}
          <div className="relative flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-4 px-3 sm:px-5 py-2.5 sm:py-3 border-t border-primary/15 bg-background/40">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-[0.6rem] sm:text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold">
                In Partnership With
              </span>
              <img
                src={puduLogo}
                alt="PUDU Robotics"
                className="w-auto h-6 sm:h-8 object-contain"
                loading="eager"
              />
            </div>
            <span
              className="inline-flex items-center gap-2 text-[0.6rem] sm:text-[0.7rem] tracking-widest uppercase border border-primary/40 px-3 py-1 rounded-full bg-background/50 font-semibold"
              style={{ color: "#d8e3ff" }}
            >
              <BadgeCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-[hsl(43_75%_55%)]" />
              Authorized PUDU Robotics Dealer
            </span>
          </div>
        </div>
      </div>

      <h1 className="neil-hero-title font-orbitron font-bold leading-[1.05] text-[clamp(28px,5vw,64px)] mt-2.5 mb-3">
        The Future of{" "}
        <span className="text-primary">Intelligent Business Automation</span>
      </h1>

      <p className="neil-hero-subtitle text-[clamp(16px,2.2vw,22px)] mb-2 max-w-[800px] leading-relaxed font-semibold text-foreground">
        Neil AI Global Solutions helps businesses overcome labor shortages, reduce employee burnout, streamline operations, and scale smarter through AI automation and advanced robotics.
      </p>
      
      <p className="text-[clamp(13px,1.8vw,16px)] mb-4 max-w-[700px] leading-relaxed text-muted-foreground">
        Empowering businesses through cutting-edge software, robotics, and intelligent automation. We streamline operations, enhance customer experiences, and reduce labor costs.
      </p>

      <div className="flex gap-3 flex-wrap items-center mt-4 mb-4">
        <Button
          asChild
          size="lg"
          className="neil-hero-cta bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 font-extrabold rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg min-h-[48px] sm:min-h-[52px] touch-manipulation transition-all duration-300 shadow-[0_0_30px_hsl(var(--primary)/0.45)] hover:shadow-[0_0_45px_hsl(var(--primary)/0.7)]"
        >
          <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Book a Free AI Demo
          </a>
        </Button>
        
        <Button 
          asChild
          variant="outline"
          size="lg"
          className="border-2 border-primary/30 hover:bg-primary/10 hover:border-primary/60 font-bold rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg min-h-[48px] sm:min-h-[52px] touch-manipulation transition-all duration-300"
        >
          <a href="/#contact" className="inline-flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Contact Us
          </a>
        </Button>

        <AIChatBox />
      </div>

      <div className="neil-trust-badges-row flex flex-wrap gap-2 my-3">
        <span className="neil-trust-badge inline-flex items-center gap-2 border border-[hsl(var(--line))] bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm transition-all duration-300 hover:border-primary/40">
          <BadgeCheck className="h-3.5 w-3.5 text-primary" />
          Authorized PUDU Dealer
        </span>
        <span className="neil-trust-badge inline-flex items-center gap-2 border border-[hsl(var(--line))] bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm transition-all duration-300 hover:border-primary/40">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" />
          Warranty &amp; Training Included
        </span>
        <span className="neil-trust-badge inline-flex items-center gap-2 border border-[hsl(var(--line))] bg-white/[0.04] hover:bg-white/[0.08] rounded-full px-3 py-1.5 font-semibold text-xs sm:text-sm transition-all duration-300 hover:border-primary/40">
          <Flag className="h-3.5 w-3.5 text-secondary" />
          U.S. Support
        </span>
      </div>

      {/* Carousel Section */}
      <div className="my-6">
        <Carousel className="w-full max-w-4xl mx-auto">
          <CarouselContent>
            <CarouselItem>
              <div className="rounded-xl overflow-hidden glass-effect border border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300">
                <video 
                  src={welcomeVideo}
                  controls
                  loop
                  muted
                  playsInline
                  className="w-full"
                  preload="metadata"
                  onError={(e) => {
                    console.log('Video failed to load, hiding element');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </CarouselItem>
            <CarouselItem>
              <div className="rounded-xl overflow-hidden">
                <AnimatedChart />
              </div>
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious className="hover:bg-primary/20 transition-colors" />
          <CarouselNext className="hover:bg-primary/20 transition-colors" />
        </Carousel>
      </div>
    </div>
  );
};

export default HeroSection;
